<script setup lang="ts">
import { computed, inject, ref } from "vue";
import TextField from "@/design/primitives/TextField.vue";
import { DISPLAY_CONTEXT, type DisplayContext } from "@/shared/contracts/ports";
import { readNumber, UnreadNumber, type WidgetProps } from "./contract";

/**
 * Numbers, integers and money.
 *
 * Emits a NUMBER, not a string: sending "12" where the column is numeric works
 * until the day someone types "12,50" and Postgres refuses the whole row. An
 * empty box emits null so a nullable column stays null rather than becoming 0 —
 * a price of zero and a price nobody set are different facts (`sob consulta`).
 */
const props = defineProps<WidgetProps & { controlId?: string; describedBy?: string }>();
const emit = defineEmits<{ "update:modelValue": [unknown]; blur: [] }>();

/**
 * O que a pessoa está digitando, intocado enquanto a caixa tem o foco: formatar a
 * cada tecla trocaria "500," por "500,00" debaixo do cursor.
 */
const draft = ref<string | null>(null);
const text = computed(() => {
	if (draft.value !== null) return draft.value;
	if (props.modelValue === null || props.modelValue === undefined) return "";
	if (props.modelValue instanceof UnreadNumber) return props.modelValue.text;
	return numberText(props.modelValue);
});
const isInteger = computed(() => props.field.type === "integer");
const isMoney = computed(() => props.field.type === "currency");

/**
 * A MARCA DA MOEDA, e por que ela não podia ser `R$`.
 *
 * Estava escrita à mão no template — `R$` sobre TODO campo de dinheiro, de todo
 * tenant, de todo país. Um negócio em Nova York digitava o preço dele com um
 * cifrão brasileiro em cima, e a mesma linha, formatada para leitura logo ao
 * lado (`field-config.ts`, `case "currency"`), saía `$1,234.56`. A caixa e a
 * lista discordavam sobre a moeda da mesma coluna.
 *
 * `formatToParts` e não uma tabela de símbolos: quem sabe que USD se escreve `$`
 * em inglês, `US$` em português e que o EUR vai depois do número em alemão é o
 * `Intl`, e uma tabela nossa seria uma segunda opinião que envelhece. Pedimos a
 * parte `currency` de um zero formatado — é o símbolo exato que a moeda tem
 * NAQUELA língua.
 */
const FALLBACK_DISPLAY: DisplayContext = {
	locale: "pt-BR",
	currency: "BRL",
	timeZone: "America/Sao_Paulo",
};
const display = inject(
	DISPLAY_CONTEXT,
	computed(() => FALLBACK_DISPLAY),
);

const currencyMark = computed(() => {
	const { locale, currency } = display.value;
	try {
		return (
			new Intl.NumberFormat(locale, { style: "currency", currency })
				.formatToParts(0)
				.find((part) => part.type === "currency")?.value ?? currency
		);
	} catch {
		// Uma moeda que o motor não conhece: o código diz mais do que um branco.
		return currency;
	}
});

/**
 * O número na caixa como a língua escreve — "500,00" e "2,5", não o "500.00" que o
 * banco devolve (QA 19/10). Sem milhar: é o que a pessoa redigita. Seis casas é o
 * que a coluna guarda; dinheiro sempre com duas.
 */
function numberText(value: unknown): string {
	const n = Number(value);
	if (!Number.isFinite(n)) return String(value);
	return new Intl.NumberFormat(display.value.locale, {
		minimumFractionDigits: isMoney.value ? 2 : 0,
		maximumFractionDigits: isMoney.value ? 2 : 6,
		useGrouping: false,
	}).format(n);
}

/**
 * Caixa de TEXTO para todo número: o `type=number` do navegador lê com a língua
 * dele e entregava "2,5" como 25 (QA 16). O que não se lê sem dúvida vai como
 * `UnreadNumber`, que a validação recusa dizendo porquê — nunca um número trocado.
 */
function change(raw: string): void {
	draft.value = raw;
	const parsed = readNumber(raw, display.value.locale, isInteger.value);
	const example = numberText(isInteger.value ? 1000 : 1234.5);
	emit(
		"update:modelValue",
		parsed === undefined ? new UnreadNumber(raw.trim(), example, isInteger.value) : parsed,
	);
}

/** Ao sair, formata o que se leu; o que não se leu fica como a pessoa escreveu. */
function leave(): void {
	if (!(props.modelValue instanceof UnreadNumber)) draft.value = null;
	emit("blur");
}
</script>

<template>
	<div class="relative">
		<!--
			The currency mark sits over the box, so it is positioned against THIS
			wrapper — the field has no label of its own here (the envelope draws it),
			so the control's middle is this wrapper's middle. `z-10`: the field's own
			wrapper is positioned too and comes later, so without it the input's
			background painted over the mark (QA 19/10: " 500,00", no R$).
		-->
		<span
			v-if="isMoney"
			class="pointer-events-none absolute left-2.5 top-1/2 z-10 -translate-y-1/2 text-sm text-fg-subtle"
		>
			{{ currencyMark }}
		</span>
		<!-- inputmode: iOS's numeric pad is digits only, with no "-" nor the thousands dot; only the text keyboard has both. -->
		<TextField
			:id="controlId"
			type="text"
			:inputmode="isInteger ? 'text' : 'decimal'"
			:model-value="text"
			:disabled="disabled"
			:invalid="Boolean(error)"
			:described-by="describedBy"
			:class="isMoney ? 'pl-9' : undefined"
			@update:model-value="change($event)"
			@blur="leave"
		/>
	</div>
</template>
